import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Bell, ChevronDown, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useNotificationsContext } from '@/context/NotificationsContext'
import { Button } from '@/components/ui/button'
import { UserMenu } from '@/components/ui/user-menu'
import { OddLogoHorizontal } from '@/components/common/OddLogoHorizontal'
import { OddLogo } from '@/components/common/OddLogo'
import { cn } from '@/lib/utils'
import { adminNavItems, type AdminNavItem } from './admin.nav'

const COLLAPSE_KEY = 'admin-sidebar-collapsed'

interface AdminSidebarProps {
  /** Force-expanded layout (e.g. inside mobile drawer). Disables collapse toggle. */
  forceExpanded?: boolean
  /** Called when any nav link is clicked — useful for closing the mobile drawer. */
  onNavigate?: () => void
}

export function AdminSidebar({ forceExpanded = false, onNavigate }: AdminSidebarProps) {
  const location = useLocation()
  const { user } = useAuth()
  const { unreadCount } = useNotificationsContext()
  const count = user ? unreadCount(user.uid) : 0

  const findActiveParent = (pathname: string) =>
    adminNavItems.find(
      (it) =>
        it.children?.length &&
        (pathname.startsWith(it.to) ||
          it.children.some((c) => pathname === c.to || pathname.startsWith(c.to + '/'))),
    )

  const [openItem, setOpenItem] = useState<string | null>(
    () => findActiveParent(location.pathname)?.to ?? null,
  )
  useEffect(() => {
    const active = findActiveParent(location.pathname)
    if (active) setOpenItem(active.to)
  }, [location.pathname])

  const [collapsed, setCollapsed] = useState(false)
  useEffect(() => {
    if (forceExpanded) return
    const stored = localStorage.getItem(COLLAPSE_KEY)
    if (stored === '1') setCollapsed(true)
  }, [forceExpanded])

  function toggleCollapsed() {
    setCollapsed((v) => {
      const next = !v
      localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0')
      return next
    })
  }

  const isCollapsed = !forceExpanded && collapsed

  return (
    <aside
      className={cn(
        'flex h-screen flex-col border-r bg-background transition-[width] duration-200',
        isCollapsed ? 'w-16' : 'w-64',
      )}
    >
      {/* Logo */}
      <div className={cn('flex h-16 shrink-0 items-center border-b px-4', isCollapsed && 'justify-center px-2')}>
        <Link to="/admin" className="flex items-center gap-2" onClick={onNavigate}>
          {isCollapsed ? (
            <OddLogo height={28} className="text-foreground" color="currentColor" />
          ) : (
            <>
              <OddLogoHorizontal height={28} className="text-foreground" color="currentColor" />
              <span className="border-l border-border pl-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Admin
              </span>
            </>
          )}
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        <ul className="flex flex-col gap-0.5">
          {adminNavItems.map((item) => (
            <SidebarItem
              key={item.to}
              item={item}
              currentPath={location.pathname}
              collapsed={isCollapsed}
              isOpen={openItem === item.to}
              onToggle={() => setOpenItem((v) => (v === item.to ? null : item.to))}
              onExpandRequest={() => {
                if (isCollapsed) toggleCollapsed()
                setOpenItem(item.to)
              }}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      </nav>

      {/* Footer: notifications, user, collapse toggle */}
      <div className="shrink-0 border-t px-2 py-2">
        <div className={cn('flex items-center gap-1', isCollapsed ? 'flex-col' : 'justify-between')}>
          <Link to="/admin/notifications" className="relative" onClick={onNavigate}>
            <Button variant="ghost" size="sm" aria-label="Notifications">
              <Bell className="h-4 w-4" />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
                  {count > 9 ? '9+' : count}
                </span>
              )}
            </Button>
          </Link>

          {user && !isCollapsed && (
            <UserMenu user={user} context="admin" size="sm" maxNameLength={20} />
          )}

          {!forceExpanded && (
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleCollapsed}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
            </Button>
          )}
        </div>
        {user && isCollapsed && (
          <div className="mt-1 flex justify-center">
            <UserMenu user={user} context="admin" size="sm" maxNameLength={2} />
          </div>
        )}
      </div>
    </aside>
  )
}

interface SidebarItemProps {
  item: AdminNavItem
  currentPath: string
  collapsed: boolean
  isOpen: boolean
  onToggle: () => void
  onExpandRequest: () => void
  onNavigate?: () => void
}

function SidebarItem({ item, currentPath, collapsed, isOpen, onToggle, onExpandRequest, onNavigate }: SidebarItemProps) {
  const Icon = item.icon
  const hasChildren = !!item.children?.length
  const isPrefixActive = !item.end && currentPath.startsWith(item.to)
  const isExactActive = currentPath === item.to
  const childActive =
    hasChildren &&
    item.children!.some((c) => currentPath === c.to || currentPath.startsWith(c.to + '/'))
  const parentActive = hasChildren && (isPrefixActive || childActive)
  const open = isOpen

  if (collapsed) {
    // Collapsed: icon-only. Parent items re-expand sidebar; leaves navigate.
    if (hasChildren) {
      return (
        <li>
          <button
            onClick={onExpandRequest}
            title={item.label}
            className={cn(
              'flex h-10 w-full items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
              parentActive && 'bg-accent text-foreground',
            )}
          >
            <Icon className="h-4 w-4" />
          </button>
        </li>
      )
    }
    return (
      <li>
        <NavLink
          to={item.to}
          end={item.end}
          title={item.label}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex h-10 w-full items-center justify-center rounded-md transition-colors',
              isActive
                ? 'bg-accent text-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )
          }
        >
          <Icon className="h-4 w-4" />
        </NavLink>
      </li>
    )
  }

  // Expanded
  if (hasChildren) {
    return (
      <li>
        <div
          className={cn(
            'overflow-hidden rounded-md transition-colors',
            open && 'border border-border bg-muted/40',
          )}
        >
          <button
            onClick={onToggle}
            className={cn(
              'flex w-full items-center gap-3 px-3 py-2 text-sm font-medium transition-colors',
              open
                ? 'text-foreground'
                : parentActive
                  ? 'rounded-md bg-accent text-foreground'
                  : 'rounded-md text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
            aria-expanded={open}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="flex-1 text-left">{item.label}</span>
            <ChevronDown
              className={cn('h-3.5 w-3.5 transition-transform duration-200', open && 'rotate-180')}
            />
          </button>
          {open && (
            <ul className="flex flex-col gap-0.5 border-t border-border/60 px-2 py-1.5">
              {item.children!.map((child) => (
                <li key={child.to}>
                  <NavLink
                    to={child.to}
                    end={child.end}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'block rounded-md px-3 py-1.5 pl-9 text-sm transition-colors',
                        isActive
                          ? 'bg-background text-brand-orange font-medium shadow-sm'
                          : 'text-muted-foreground hover:bg-background/60 hover:text-foreground',
                      )
                    }
                  >
                    {child.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </div>
      </li>
    )
  }

  return (
    <li>
      <NavLink
        to={item.to}
        end={item.end}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
            isActive || isExactActive
              ? 'bg-accent text-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-foreground',
          )
        }
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span>{item.label}</span>
      </NavLink>
    </li>
  )
}
